import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';

import { filterOptionsQuery } from '$/services/usecases/filter/queries';
import { useCollectionStore } from '$/stores/useCollectionStore';
import { unsecureHashCode } from '$/utils/numberUtils';

export const useFilterOptions = () => {
  const appliedFilterOptions = useCollectionStore.useAppliedFilterOptions();
  const activeType = useCollectionStore.useActiveType();

  const { data, isLoading, isFetching, isPlaceholderData } = useQuery(
    filterOptionsQuery({ type: activeType, filter: appliedFilterOptions }),
  );

  useEffect(() => {
    if (!data || isFetching || isPlaceholderData) return;

    useCollectionStore.setState((state) => {
      const options = [...data.additional, ...data.general]
        .flat()
        .reduce<Record<string, (string | number)[]>>((acc, option) => {
          acc[option.searchField] = option.items.map((item) => item.value);
          return acc;
        }, {});

      const activeFilterGroups = state.appliedFilterOptions
        .map((appliedFilter) => {
          const filterOptionValues = options[appliedFilter.option];
          if (!filterOptionValues) return undefined;

          if (!Array.isArray(appliedFilter.value)) {
            if (filterOptionValues.includes(appliedFilter.value)) {
              return appliedFilter;
            }

            return undefined;
          }

          const filteredValues = {
            option: appliedFilter.option,
            value: appliedFilter.value.filter((value) =>
              filterOptionValues.includes(value),
            ),
          };

          if (filteredValues.value.length === 0) return undefined;

          return filteredValues;
        })
        .filter((appliedFilter) => appliedFilter != undefined);

      const originalHash = unsecureHashCode(JSON.stringify(activeFilterGroups));
      const newHash = unsecureHashCode(
        JSON.stringify(state.appliedFilterOptions),
      );

      if (originalHash === newHash) return state;

      return { appliedFilterOptions: activeFilterGroups };
    });
  }, [data, isFetching, isPlaceholderData]);

  return useMemo(() => ({ data, isLoading }), [data, isLoading]);
};

export const useFilterOptionsNoSideEffects = () => {
  const appliedFilterOptions = useCollectionStore.useAppliedFilterOptions();
  const activeType = useCollectionStore.useActiveType();

  const { data, isLoading } = useQuery(
    filterOptionsQuery({ type: activeType, filter: appliedFilterOptions }),
  );

  return useMemo(() => ({ data, isLoading }), [data, isLoading]);
};
